import { useEffect, useState } from 'react';
import { Save, Loader2, Upload, Image } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContentManager() {
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingHero, setUploadingHero] = useState(false);
  const [uploadingBooking, setUploadingBooking] = useState(false);

  useEffect(() => {
    fetchProperty();
  }, []);

  const fetchProperty = async () => {
    try {
      const res = await axios.get(`${API}/property`);
      setProperty(res.data);
    } catch (error) {
      toast.error('Failed to load property data');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setProperty(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/property`, property);
      toast.success('Property information saved');
    } catch (error) {
      toast.error('Failed to save property');
    } finally {
      setSaving(false);
    }
  };

  const handleHeroUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    setUploadingHero(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('alt', 'Homepage Hero Background');
    formData.append('category', 'hero');

    try {
      const res = await axios.post(`${API}/gallery/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      // Update the property with the new hero image URL
      const imageUrl = res.data.url;
      setProperty(prev => ({ ...prev, hero_image: imageUrl }));
      
      // Auto-save the property with new hero image
      await axios.put(`${API}/property`, { ...property, hero_image: imageUrl });
      toast.success('Homepage background updated!');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingHero(false);
    }
  };

  const handleBookingImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    setUploadingBooking(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('alt', 'Booking Page Property Image');
    formData.append('category', 'booking');

    try {
      const res = await axios.post(`${API}/gallery/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      // Update the property with the new booking image URL
      const imageUrl = res.data.url;
      setProperty(prev => ({ ...prev, booking_page_image: imageUrl }));
      
      // Auto-save the property with new booking image
      await axios.put(`${API}/property`, { ...property, booking_page_image: imageUrl });
      toast.success('Booking page image updated!');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingBooking(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-[#1B4D3E]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="font-serif text-3xl text-[#1C1C1C] mb-2">Property Content</h1>
          <p className="text-[#66605B]">Edit property information and details</p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#1B4D3E] hover:bg-[#153E32]"
          data-testid="save-content-btn"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Save Changes
        </Button>
      </div>

      {/* Homepage Background Image */}
      <div className="bg-white rounded-sm shadow-sm p-6">
        <h2 className="font-serif text-xl text-[#1C1C1C] mb-4 flex items-center gap-2">
          <Image className="w-5 h-5 text-[#1B4D3E]" />
          Homepage Background
        </h2>
        <div className="space-y-4">
          {property?.hero_image && (
            <div className="relative rounded-lg overflow-hidden h-48 w-full max-w-xl">
              <img 
                src={property.hero_image.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${property.hero_image}` : property.hero_image}
                alt="Current homepage background"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                <span className="text-white text-sm font-medium">Current Background</span>
              </div>
            </div>
          )}
          
          <div className="flex flex-col sm:flex-row gap-4 items-start">
            <div className="flex-1 space-y-2">
              <Label htmlFor="hero_image_url">Image URL</Label>
              <Input
                id="hero_image_url"
                value={property?.hero_image || ''}
                onChange={(e) => handleChange('hero_image', e.target.value)}
                placeholder="Enter image URL or upload below"
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label>Or Upload</Label>
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleHeroUpload}
                  className="hidden"
                  disabled={uploadingHero}
                />
                <div className="flex items-center gap-2 px-4 py-2 bg-[#1B4D3E] text-white rounded-md hover:bg-[#153E32] transition-colors">
                  {uploadingHero ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4" />
                  )}
                  <span>{uploadingHero ? 'Uploading...' : 'Upload Image'}</span>
                </div>
              </label>
            </div>
          </div>
          <p className="text-xs text-[#66605B]">
            This image will be displayed as the full-screen background on the homepage hero section.
            Recommended size: 1920x1080 or larger.
          </p>
        </div>
      </div>

      {/* Booking Page Image */}
      <div className="bg-white rounded-sm shadow-sm p-6">
        <h2 className="font-serif text-xl text-[#1C1C1C] mb-4 flex items-center gap-2">
          <Image className="w-5 h-5 text-[#1B4D3E]" />
          Booking Page Image
        </h2>
        <div className="space-y-4">
          {property?.booking_page_image && (
            <div className="relative rounded-lg overflow-hidden h-32 w-48">
              <img 
                src={property.booking_page_image.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${property.booking_page_image}` : property.booking_page_image}
                alt="Current booking page image"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                <span className="text-white text-xs font-medium">Current Image</span>
              </div>
            </div>
          )}
          
          <div className="flex flex-col sm:flex-row gap-4 items-start">
            <div className="flex-1 space-y-2">
              <Label htmlFor="booking_image_url">Image URL</Label>
              <Input
                id="booking_image_url"
                value={property?.booking_page_image || ''}
                onChange={(e) => handleChange('booking_page_image', e.target.value)}
                placeholder="Enter image URL or upload below"
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label>Or Upload</Label>
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleBookingImageUpload}
                  className="hidden"
                  disabled={uploadingBooking}
                />
                <div className="flex items-center gap-2 px-4 py-2 bg-[#1B4D3E] text-white rounded-md hover:bg-[#153E32] transition-colors">
                  {uploadingBooking ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4" />
                  )}
                  <span>{uploadingBooking ? 'Uploading...' : 'Upload Image'}</span>
                </div>
              </label>
            </div>
          </div>
          <p className="text-xs text-[#66605B]">
            This thumbnail image appears on the booking page next to the property summary.
            Recommended size: 400x300 or similar aspect ratio.
          </p>
        </div>
      </div>

      <div className="bg-white rounded-sm shadow-sm p-6 space-y-6">
        {/* Basic Info */}
        <div>
          <h2 className="font-serif text-xl text-[#1C1C1C] mb-4">Basic Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="name">Property Name</Label>
              <Input
                id="name"
                value={property?.name || ''}
                onChange={(e) => handleChange('name', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tagline">Tagline</Label>
              <Input
                id="tagline"
                value={property?.tagline || ''}
                onChange={(e) => handleChange('tagline', e.target.value)}
                placeholder="A Retreat From The Ordinary"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="location">Location</Label>
              <Input
                id="location"
                value={property?.location || ''}
                onChange={(e) => handleChange('location', e.target.value)}
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={property?.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
                rows={5}
              />
            </div>
          </div>
        </div>

        {/* Property Stats */}
        <div>
          <h2 className="font-serif text-xl text-[#1C1C1C] mb-4">Property Details</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="space-y-2">
              <Label htmlFor="bedrooms">Bedrooms</Label>
              <Input
                id="bedrooms"
                type="number"
                value={property?.bedrooms || 0}
                onChange={(e) => handleChange('bedrooms', parseInt(e.target.value))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="bathrooms">Bathrooms</Label>
              <Input
                id="bathrooms"
                type="number"
                value={property?.bathrooms || 0}
                onChange={(e) => handleChange('bathrooms', parseInt(e.target.value))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sleeps">Sleeps</Label>
              <Input
                id="sleeps"
                type="number"
                value={property?.sleeps || 0}
                onChange={(e) => handleChange('sleeps', parseInt(e.target.value))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sqft">Square Feet</Label>
              <Input
                id="sqft"
                type="number"
                value={property?.sqft || 0}
                onChange={(e) => handleChange('sqft', parseInt(e.target.value))}
              />
            </div>
          </div>
        </div>

        {/* Check-in/out */}
        <div>
          <h2 className="font-serif text-xl text-[#1C1C1C] mb-4">Check-in / Check-out</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="check_in">Check-in Time</Label>
              <Input
                id="check_in"
                value={property?.check_in || ''}
                onChange={(e) => handleChange('check_in', e.target.value)}
                placeholder="Friday 4:30 pm"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="check_out">Check-out Time</Label>
              <Input
                id="check_out"
                value={property?.check_out || ''}
                onChange={(e) => handleChange('check_out', e.target.value)}
                placeholder="Friday 11:00 am"
              />
            </div>
          </div>
        </div>

        {/* Contact */}
        <div>
          <h2 className="font-serif text-xl text-[#1C1C1C] mb-4">Contact Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="contact_phone">Phone Number</Label>
              <Input
                id="contact_phone"
                value={property?.contact_phone || ''}
                onChange={(e) => handleChange('contact_phone', e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Save Button (Bottom) */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#1B4D3E] hover:bg-[#153E32]"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Save Changes
        </Button>
      </div>
    </div>
  );
}
